/**
 * Activity Card Component
 *
 * Displays a single activity in the timeline
 */

import {
  Phone,
  Users,
  Mail,
  Search,
  Presentation,
  StickyNote,
  Clock,
  CheckCircle2,
  XCircle,
  MoreVertical,
} from 'lucide-react';
import DOMPurify from 'dompurify';
import { cn } from '@/lib/utils';
import { formatDateTime } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { CrmActivityDetail, CrmActivityType, CrmActivityStatus } from '../../types';

interface ActivityCardProps {
  activity: CrmActivityDetail;
  variant?: 'timeline' | 'compact';
  onComplete?: (activity: CrmActivityDetail) => void;
  onEdit?: (activity: CrmActivityDetail) => void;
  onDelete?: (activity: CrmActivityDetail) => void;
}

const ACTIVITY_ICONS: Record<CrmActivityType, React.ComponentType<{ className?: string }>> = {
  call: Phone,
  meeting: Users,
  email: Mail,
  discovery_session: Search,
  proposal_presentation: Presentation,
  note: StickyNote,
};

const ACTIVITY_COLORS: Record<CrmActivityType, string> = {
  call: 'bg-blue-100 text-blue-600',
  meeting: 'bg-violet-100 text-violet-600',
  email: 'bg-cyan-100 text-cyan-600',
  discovery_session: 'bg-amber-100 text-amber-600',
  proposal_presentation: 'bg-orange-100 text-orange-600',
  note: 'bg-gray-100 text-gray-600',
};

const STATUS_STYLES: Record<CrmActivityStatus, { icon: React.ComponentType<{ className?: string }>; color: string }> = {
  scheduled: { icon: Clock, color: 'text-blue-500' },
  completed: { icon: CheckCircle2, color: 'text-green-500' },
  cancelled: { icon: XCircle, color: 'text-gray-400' },
};

const OUTCOME_BADGES: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  positive: { label: 'Positive', variant: 'default' },
  neutral: { label: 'Neutral', variant: 'secondary' },
  negative: { label: 'Negative', variant: 'destructive' },
  no_show: { label: 'No Show', variant: 'outline' },
};

export function ActivityCard({
  activity,
  variant = 'timeline',
  onComplete,
  onEdit,
  onDelete,
}: ActivityCardProps) {
  const Icon = ACTIVITY_ICONS[activity.type] || StickyNote;
  const iconColorClass = ACTIVITY_COLORS[activity.type] || 'bg-gray-100 text-gray-600';
  const StatusIcon = STATUS_STYLES[activity.status]?.icon || Clock;
  const statusColor = STATUS_STYLES[activity.status]?.color || 'text-gray-500';

  const displayDate = activity.completed_at || activity.scheduled_at;
  const isScheduled = activity.status === 'scheduled';
  const isPast = activity.scheduled_at && new Date(activity.scheduled_at) < new Date();

  if (variant === 'compact') {
    return (
      <div className="flex items-center gap-3 py-2">
        <div className={cn('p-1.5 rounded-lg', iconColorClass)}>
          <Icon className="h-3.5 w-3.5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{activity.title}</p>
          {displayDate && (
            <p className="text-xs text-gray-500">{formatDateTime(displayDate)}</p>
          )}
        </div>
        <StatusIcon className={cn('h-4 w-4', statusColor)} />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'relative pl-8 pb-6',
        'before:absolute before:left-[11px] before:top-8 before:bottom-0 before:w-0.5 before:bg-gray-200',
        'last:before:hidden last:pb-0'
      )}
    >
      {/* Timeline dot */}
      <div
        className={cn(
          'absolute left-0 top-0 p-2 rounded-full',
          iconColorClass
        )}
      >
        <Icon className="h-4 w-4" />
      </div>

      {/* Content */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 ml-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex-1">
            {/* Notes have no title in HubSpot — the body is the content. Other
                activity types keep their title + scheduling/status metadata. */}
            {activity.type === 'note' ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span>{formatDateTime(activity.created_at)}</span>
              </div>
            ) : (
              <>
                <h4 className="font-medium text-gray-900">{activity.title}</h4>
                <div className="flex items-center gap-2 mt-1">
                  <StatusIcon className={cn('h-4 w-4', statusColor)} />
                  <span className="text-sm text-gray-500 capitalize">{activity.status}</span>
                  {displayDate && (
                    <>
                      <span className="text-gray-300">|</span>
                      <span className="text-sm text-gray-500">{formatDateTime(displayDate)}</span>
                    </>
                  )}
                  {activity.duration_minutes && (
                    <>
                      <span className="text-gray-300">|</span>
                      <span className="text-sm text-gray-500">{activity.duration_minutes} min</span>
                    </>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Actions dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {isScheduled && onComplete && (
                <DropdownMenuItem onClick={() => onComplete(activity)}>
                  Mark Complete
                </DropdownMenuItem>
              )}
              {onEdit && (
                <DropdownMenuItem onClick={() => onEdit(activity)}>
                  Edit
                </DropdownMenuItem>
              )}
              {onDelete && (
                <DropdownMenuItem
                  onClick={() => onDelete(activity)}
                  className="text-red-600"
                >
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Description / note body. Notes are rich text (HTML) — sanitize
            before rendering. Other types are plain text. */}
        {activity.description &&
          (activity.type === 'note' ? (
            <div
              className="prose prose-sm max-w-none text-gray-700 mb-3 [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(activity.description) }}
            />
          ) : (
            <p className="text-sm text-gray-600 mb-3">{activity.description}</p>
          ))}

        {/* Outcome (for completed activities) */}
        {activity.status === 'completed' && activity.outcome && (
          <div className="mb-3">
            <Badge variant={OUTCOME_BADGES[activity.outcome]?.variant || 'secondary'}>
              {OUTCOME_BADGES[activity.outcome]?.label || activity.outcome}
            </Badge>
            {activity.outcome_notes && (
              <p className="text-sm text-gray-600 mt-2">{activity.outcome_notes}</p>
            )}
          </div>
        )}

        {/* Overdue indicator */}
        {isScheduled && isPast && (
          <div className="mt-2 text-xs text-red-600 bg-red-50 rounded px-2 py-1 inline-block">
            Overdue
          </div>
        )}

        {/* Created by */}
        {activity.created_by_name && (
          <p className="text-xs text-gray-400 mt-2">
            Created by {activity.created_by_name}
          </p>
        )}
      </div>
    </div>
  );
}
